import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import type { ManualMetricKey } from "@/api/fp-development";
import { useSession } from "@/auth/client";
import {
  formatCurrency,
  formatMonthLabel,
  formatNumber,
  formatPercent,
} from "@/components/deal-metrics/shared";
import { formatLongMonthLabel } from "@/components/executive/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { currentFiscalYear, formatFY } from "@/lib/fiscal";
import { GOAL_TEXT, goalStatus } from "@/lib/goal-status";
import { cn } from "@/lib/utils";

// ── Types ─────────────────────────────────────────────────────────────

interface MetricCell {
  numerator: number;
  denominator: number;
  value: number | null;
}

interface TrackerRow {
  opportunities: number;
  closedDeals: number;
  closeRate: number;
  grossSales: number;
  netSales: number;
}

interface OpsRow {
  accuracyRate: MetricCell;
  qualityRate: MetricCell;
  qualityErrorRate: MetricCell;
  otdRate: MetricCell;
  repairResizeDays: MetricCell;
}

interface MonthlyMetricsRow {
  month: string;
  tracker: TrackerRow | null;
  ops: OpsRow | null;
  manual: Partial<Record<ManualMetricKey, number>>;
  npsScore: number | null;
  googleReviews: number | null;
  affiliatesAdded: number | null;
  relationalOpportunities: number;
  digitalOpportunities: number;
  prospectingOpportunities: number;
  youtubeOpportunities: number;
  campusesTargeted: number;
  caseStudyOpportunities: number;
  tiktokOpportunities: number;
  campusOpportunities: number;
  instagramOpportunities: number;
  googleOpportunities: number;
  emailOpportunities: number;
  repeatClientOpportunities: number;
  affiliateOpportunities: number;
  otherRelationalOpportunities: number;
  friendOpportunities: number;
  instagramFollowers: number | null;
  websiteVisitors: number | null;
  emailSubscribers: number | null;
  youtubeSubscribers: number | null;
  productionCallOne: number;
  repeatClients: number;
  initialResponseTime: number | null;
}

interface YoyMetricValues {
  opportunities: number | null;
  opportunitiesYoy: number | null;
  closedDeals: number;
  closedDealsYoy: number | null;
  grossSales: number;
  grossSalesYoy: number | null;
  netSales: number;
  netSalesYoy: number | null;
  avgOrderValue: number;
  grossProfitMargin: number;
  closeRate: number | null;
}

interface YoyMetricsRow extends YoyMetricValues {
  month: string;
}

interface YoyYearRow extends YoyMetricValues {
  year: number;
}

interface YoyMetricsResponse {
  rows: YoyMetricsRow[];
  years: YoyYearRow[];
  currentYearProjection: YoyYearRow | null;
  error?: string;
}

interface MonthlyMetricsResponse {
  fiscalYear: number;
  months: string[];
  rows: MonthlyMetricsRow[];
  error?: string;
}

// ── Goals & goal helpers ──────────────────────────────────────────────

type GoalComparison = "gte" | "lte";
interface MonthlyGoal {
  value: number;
  comparison: GoalComparison;
  format: "count" | "currency" | "percent" | "decimal";
}

function formatGoalValue(value: number, format: MonthlyGoal["format"]): string {
  switch (format) {
    case "count":
      return formatNumber(Math.round(value));
    case "currency":
      return formatCurrency(value);
    case "percent":
      return formatPercent(value);
    case "decimal":
      return value.toFixed(1);
  }
}

// ── Row definitions ───────────────────────────────────────────────────

interface MetricRowDef {
  key: string;
  label: string;
  manualKey?: ManualMetricKey;
  getValue?: (row: MonthlyMetricsRow) => string;
  getNumber: (row: MonthlyMetricsRow) => number | null;
  aggregate: "sum" | "avg";
  format: (n: number) => string;
  /** True for count/sum metrics that should be projected linearly by days elapsed. */
  projectByDays?: boolean;
  goal?: MonthlyGoal;
  /** Annual year-end target displayed in the Year End Goal column. */
  yearEndGoal?: { value: number; format: MonthlyGoal["format"]; label?: string };
  /** Supporting activity metrics shown in an expandable drawer under this row. */
  details?: MetricRowDef[];
}

interface SectionDef {
  title: string;
  headerClass: string;
  rows: MetricRowDef[];
}

const SECTIONS: SectionDef[] = [
  {
    title: "Marketing (Demand Creation)",
    headerClass: "bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300",
    rows: [
      {
        key: "instagram_opportunities",
        label: "FP Instagram Opportunities",
        getValue: (row) => formatNumber(row.instagramOpportunities),
        getNumber: (row) => row.instagramOpportunities,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        goal: { value: 65, comparison: "gte", format: "count" },
        yearEndGoal: { value: 785, format: "count" },
        details: [
          {
            key: "instagram_followers",
            label: "FP Instagram Followers",
            getValue: (row) =>
              row.instagramFollowers != null ? formatNumber(row.instagramFollowers) : "—",
            getNumber: (row) => row.instagramFollowers,
            aggregate: "sum",
            format: formatNumber,
            projectByDays: true,
            goal: { value: 500, comparison: "gte", format: "count" },
            yearEndGoal: { value: 6000, format: "count" },
          },
        ],
      },
      {
        key: "google_opportunities",
        label: "Google Opportunities",
        getValue: (row) => formatNumber(row.googleOpportunities),
        getNumber: (row) => row.googleOpportunities,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        goal: { value: 26, comparison: "gte", format: "count" },
        yearEndGoal: { value: 312, format: "count" },
        details: [
          {
            key: "google_ranking",
            label: "Google Ranking",
            manualKey: "google_ranking",
            getValue: (row) =>
              row.manual.google_ranking != null ? String(row.manual.google_ranking) : "—",
            getNumber: (row) => row.manual.google_ranking ?? null,
            aggregate: "avg",
            format: (n) => n.toFixed(0),
            projectByDays: false,
          },
        ],
      },
      {
        key: "email_opportunities",
        label: "Email Opportunities",
        getValue: (row) => formatNumber(row.emailOpportunities),
        getNumber: (row) => row.emailOpportunities,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        // Email marketing was never built out, so there is no lead goal for it.
        // Digital + FP Total below are net of this row's original 25/mo, 300/yr.
        goal: { value: 0, comparison: "gte", format: "count" },
        yearEndGoal: { value: 0, format: "count" },
        details: [
          {
            key: "email_subscribers",
            label: "Email Subscribers",
            getValue: (row) =>
              row.emailSubscribers != null ? formatNumber(row.emailSubscribers) : "—",
            getNumber: (row) => row.emailSubscribers,
            aggregate: "sum",
            format: formatNumber,
            projectByDays: true,
            goal: { value: 750, comparison: "gte", format: "count" },
            yearEndGoal: { value: 10000, format: "count" },
          },
        ],
      },
      {
        key: "digital_opportunities",
        label: "Digital Opportunities",
        getValue: (row) => formatNumber(row.digitalOpportunities),
        getNumber: (row) => row.digitalOpportunities,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        // Instagram + Google only — email's 25/mo, 300/yr was removed.
        goal: { value: 97, comparison: "gte", format: "count" },
        yearEndGoal: { value: 1097, format: "count" },
      },
    ],
  },
  {
    title: "Prospecting",
    headerClass: "bg-purple-50 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300",
    rows: [
      {
        key: "youtube_opportunities",
        label: "YouTube Opportunities",
        getValue: (row) => formatNumber(row.youtubeOpportunities),
        getNumber: (row) => row.youtubeOpportunities,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        goal: { value: 70, comparison: "gte", format: "count" },
        yearEndGoal: { value: 850, format: "count" },
        details: [
          {
            key: "youtube_subscribers",
            label: "Total YouTube Subscribers",
            getValue: (row) =>
              row.youtubeSubscribers != null ? formatNumber(row.youtubeSubscribers) : "—",
            getNumber: (row) => row.youtubeSubscribers,
            aggregate: "avg",
            format: formatNumber,
            projectByDays: false,
            goal: { value: 83, comparison: "gte", format: "count" },
          },
        ],
      },
      {
        key: "campus_opportunities",
        label: "College Campus Opportunities",
        getValue: (row) => formatNumber(row.campusOpportunities),
        getNumber: (row) => row.campusOpportunities,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        goal: { value: 29, comparison: "gte", format: "count" },
        yearEndGoal: { value: 350, format: "count" },
        details: [
          {
            key: "campuses_targeted",
            label: "Campuses Targeted",
            getValue: (row) => formatNumber(row.campusesTargeted),
            getNumber: (row) => row.campusesTargeted,
            aggregate: "avg",
            format: formatNumber,
            projectByDays: false,
          },
          {
            key: "campus_connections",
            label: "Monthly Campus Connections",
            manualKey: "campus_connections",
            getValue: (row) =>
              row.manual.campus_connections != null
                ? formatNumber(row.manual.campus_connections)
                : "—",
            getNumber: (row) => row.manual.campus_connections ?? null,
            aggregate: "sum",
            format: formatNumber,
            projectByDays: true,
            goal: { value: 29, comparison: "gte", format: "count" },
            yearEndGoal: { value: 350, format: "count" },
          },
        ],
      },
      {
        key: "case_study_opportunities",
        label: "Case Study Opportunities",
        getValue: (row) => formatNumber(row.caseStudyOpportunities),
        getNumber: (row) => row.caseStudyOpportunities,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        goal: { value: 25, comparison: "gte", format: "count" },
        yearEndGoal: { value: 300, format: "count" },
        details: [
          {
            key: "case_study_participants",
            label: "Monthly Case Study Participants",
            manualKey: "case_study_participants",
            getValue: (row) =>
              row.manual.case_study_participants != null
                ? formatNumber(row.manual.case_study_participants)
                : "—",
            getNumber: (row) => row.manual.case_study_participants ?? null,
            aggregate: "sum",
            format: formatNumber,
            projectByDays: true,
            goal: { value: 42, comparison: "gte", format: "count" },
            yearEndGoal: { value: 500, format: "count" },
          },
        ],
      },
      {
        key: "diamond_dude_tiktok_opportunities",
        label: "Diamond Dude TikTok Opportunities",
        getValue: (row) => formatNumber(row.tiktokOpportunities),
        getNumber: (row) => row.tiktokOpportunities,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        goal: { value: 33, comparison: "gte", format: "count" },
        yearEndGoal: { value: 395, format: "count" },
        details: [
          {
            key: "tiktok_followers",
            label: "New TikTok Followers",
            manualKey: "tiktok_followers",
            getValue: (row) =>
              row.manual.tiktok_followers != null ? formatNumber(row.manual.tiktok_followers) : "—",
            getNumber: (row) => row.manual.tiktok_followers ?? null,
            aggregate: "sum",
            format: formatNumber,
            projectByDays: true,
            goal: { value: 167, comparison: "gte", format: "count" },
            yearEndGoal: { value: 6000, format: "count" },
          },
        ],
      },
      {
        key: "prospecting_opportunities",
        label: "Total Prospecting Opportunities",
        getValue: (row) => formatNumber(row.prospectingOpportunities),
        getNumber: (row) => row.prospectingOpportunities,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        goal: { value: 158, comparison: "gte", format: "count" },
        yearEndGoal: { value: 1895, format: "count" },
      },
    ],
  },
  {
    title: "Consultants",
    headerClass: "bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
    rows: [
      {
        key: "relational_opportunities",
        label: "Relational Opportunities",
        getValue: (row) => formatNumber(row.relationalOpportunities),
        getNumber: (row) => row.relationalOpportunities,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        goal: { value: 549, comparison: "gte", format: "count" },
        yearEndGoal: { value: 6590, format: "count" },
        details: [
          {
            key: "repeat_client_opportunities",
            label: "Repeat Client Opportunities",
            getValue: (row) => formatNumber(row.repeatClientOpportunities),
            getNumber: (row) => row.repeatClientOpportunities,
            aggregate: "sum",
            format: formatNumber,
            projectByDays: true,
            goal: { value: 167, comparison: "gte", format: "count" },
            yearEndGoal: { value: 2000, format: "count" },
          },
          {
            key: "affiliate_opportunities",
            label: "Affiliate Opportunities",
            getValue: (row) => formatNumber(row.affiliateOpportunities),
            getNumber: (row) => row.affiliateOpportunities,
            aggregate: "sum",
            format: formatNumber,
            projectByDays: true,
            goal: { value: 100, comparison: "gte", format: "count" },
            yearEndGoal: { value: 1200, format: "count" },
          },
          {
            key: "friend_and_family_opportunities",
            label: "Friend and Family Opportunities",
            getValue: (row) => formatNumber(row.friendOpportunities),
            getNumber: (row) => row.friendOpportunities,
            aggregate: "sum",
            format: formatNumber,
            projectByDays: true,
            goal: { value: 250, comparison: "gte", format: "count" },
            yearEndGoal: { value: 3000, format: "count" },
          },
          {
            key: "other_opportunities",
            label: "Other Opportunities",
            getValue: (row) => formatNumber(row.otherRelationalOpportunities),
            getNumber: (row) => row.otherRelationalOpportunities,
            aggregate: "sum",
            format: formatNumber,
            projectByDays: true,
            goal: { value: 18, comparison: "gte", format: "count" },
            yearEndGoal: { value: 210, format: "count" },
          },
        ],
      },
      {
        key: "opportunities",
        label: "FP Total Opportunities",
        getValue: (row) => (row.tracker ? formatNumber(row.tracker.opportunities) : "—"),
        getNumber: (row) => row.tracker?.opportunities ?? null,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        // Digital (1,097) + Prospecting (1,895) + Relational (6,590), i.e. the
        // old 9,882 less email's 300.
        goal: { value: 799, comparison: "gte", format: "count" },
        yearEndGoal: { value: 9582, format: "count" },
        details: [
          {
            key: "website_visitors",
            label: "Website Visitors",
            getValue: (row) =>
              row.websiteVisitors != null ? formatNumber(row.websiteVisitors) : "—",
            getNumber: (row) => row.websiteVisitors,
            aggregate: "sum",
            format: formatNumber,
            projectByDays: true,
            goal: { value: 11000, comparison: "gte", format: "count" },
            yearEndGoal: { value: 120000, format: "count" },
          },
        ],
      },
      {
        key: "initial_response_time",
        label: "Initial Response Time",
        manualKey: "initial_response_time",
        getValue: (row) =>
          row.manual.initial_response_time != null
            ? `${row.manual.initial_response_time.toFixed(0)} min`
            : "—",
        getNumber: (row) => row.manual.initial_response_time ?? null,
        aggregate: "avg",
        format: (n) => `${n.toFixed(0)} min`,
        projectByDays: false,
        goal: { value: 5, comparison: "lte", format: "decimal" },
        yearEndGoal: { value: 10, format: "decimal", label: "10 min" },
      },
      {
        key: "consultant_phone_call",
        label: "Initial Phone Call",
        manualKey: "consultant_phone_call",
        getValue: (row) =>
          row.manual.consultant_phone_call != null && row.tracker && row.tracker.opportunities > 0
            ? formatPercent(row.manual.consultant_phone_call / row.tracker.opportunities)
            : "—",
        getNumber: (row) =>
          row.manual.consultant_phone_call != null && row.tracker && row.tracker.opportunities > 0
            ? row.manual.consultant_phone_call / row.tracker.opportunities
            : null,
        aggregate: "avg",
        format: formatPercent,
        projectByDays: false,
        goal: { value: 0.8, comparison: "gte", format: "percent" },
        yearEndGoal: { value: 0.7, format: "percent" },
      },
      {
        key: "closedDeals",
        label: "FP Closed Deals",
        getValue: (row) => (row.tracker ? formatNumber(row.tracker.closedDeals) : "—"),
        getNumber: (row) => row.tracker?.closedDeals ?? null,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        goal: { value: 473, comparison: "gte", format: "count" },
        yearEndGoal: { value: 6000, format: "count" },
      },
      {
        key: "closeRate",
        label: "Close Rate",
        getValue: (row) => (row.tracker ? formatPercent(row.tracker.closeRate) : "—"),
        getNumber: (row) => row.tracker?.closeRate ?? null,
        aggregate: "avg",
        format: formatPercent,
        projectByDays: false,
        goal: { value: 0.6, comparison: "gte", format: "percent" },
        yearEndGoal: { value: 0.6, format: "percent" },
      },
      {
        key: "grossSales",
        label: "Gross Sales",
        getValue: (row) => (row.tracker ? formatCurrency(row.tracker.grossSales) : "—"),
        getNumber: (row) => row.tracker?.grossSales ?? null,
        aggregate: "sum",
        format: formatCurrency,
        projectByDays: true,
        goal: { value: 833333, comparison: "gte", format: "currency" },
        yearEndGoal: { value: 10000000, format: "currency" },
      },
      {
        key: "netSales",
        label: "Net Sales",
        getValue: (row) => (row.tracker ? formatCurrency(row.tracker.netSales) : "—"),
        getNumber: (row) => row.tracker?.netSales ?? null,
        aggregate: "sum",
        format: formatCurrency,
        projectByDays: true,
        goal: { value: 333333, comparison: "gte", format: "currency" },
        yearEndGoal: { value: 4000000, format: "currency" },
      },
      {
        key: "netSalesPct",
        label: "Net Sales Percentage",
        getValue: (row) =>
          row.tracker && row.tracker.grossSales > 0
            ? formatPercent(row.tracker.netSales / row.tracker.grossSales)
            : "—",
        getNumber: (row) =>
          row.tracker && row.tracker.grossSales > 0
            ? row.tracker.netSales / row.tracker.grossSales
            : null,
        aggregate: "avg",
        format: formatPercent,
        projectByDays: false,
        goal: { value: 0.4, comparison: "gte", format: "percent" },
        yearEndGoal: { value: 0.4, format: "percent" },
      },
      {
        key: "qualityErrorRate",
        label: "Error Rate",
        getValue: (row) =>
          row.ops?.qualityErrorRate.value != null
            ? formatPercent(row.ops.qualityErrorRate.value)
            : "—",
        getNumber: (row) => row.ops?.qualityErrorRate.value ?? null,
        aggregate: "avg",
        format: formatPercent,
        projectByDays: false,
        goal: { value: 0.0005, comparison: "lte", format: "percent" },
        yearEndGoal: { value: 0.001, format: "percent", label: "<0.1%" },
      },
      {
        key: "design_time",
        label: "Initial Design Time",
        manualKey: "design_time",
        getValue: (row) =>
          row.manual.design_time != null ? `${row.manual.design_time.toFixed(1)} hrs` : "—",
        getNumber: (row) => row.manual.design_time ?? null,
        aggregate: "avg",
        format: (n) => `${n.toFixed(1)} hrs`,
        projectByDays: false,
        goal: { value: 24, comparison: "lte", format: "decimal" },
        yearEndGoal: { value: 24, format: "decimal", label: "<24 hrs" },
      },
      {
        key: "average_redesigns",
        label: "Average Number of Redesigns",
        manualKey: "average_redesigns",
        getValue: (row) =>
          row.manual.average_redesigns != null ? row.manual.average_redesigns.toFixed(1) : "—",
        getNumber: (row) => row.manual.average_redesigns ?? null,
        aggregate: "avg",
        format: (n) => n.toFixed(1),
        projectByDays: false,
        goal: { value: 1.5, comparison: "lte", format: "decimal" },
        yearEndGoal: { value: 1.5, format: "decimal" },
      },
    ],
  },
  {
    title: "Operations",
    headerClass: "bg-orange-50 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300",
    rows: [
      {
        key: "production_phone_call",
        label: "Production Phone Call",
        getValue: (row) => formatNumber(row.productionCallOne),
        getNumber: (row) => row.productionCallOne,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        goal: { value: 187, comparison: "gte", format: "count" },
        yearEndGoal: { value: 2000, format: "count" },
      },
      {
        key: "accuracyRate",
        label: "Accuracy Rate",
        getValue: (row) =>
          row.ops?.accuracyRate.value != null ? formatPercent(row.ops.accuracyRate.value) : "—",
        getNumber: (row) => row.ops?.accuracyRate.value ?? null,
        aggregate: "avg",
        format: formatPercent,
        projectByDays: false,
        goal: { value: 0.99, comparison: "gte", format: "percent" },
      },
      {
        key: "qualityRate",
        label: "Quality Rate",
        getValue: (row) =>
          row.ops?.qualityRate.value != null ? formatPercent(row.ops.qualityRate.value) : "—",
        getNumber: (row) => row.ops?.qualityRate.value ?? null,
        aggregate: "avg",
        format: formatPercent,
        projectByDays: false,
        goal: { value: 0.99, comparison: "gte", format: "percent" },
      },
      {
        key: "otdRate",
        label: "On Time Delivery Rate",
        getValue: (row) =>
          row.ops?.otdRate.value != null ? formatPercent(row.ops.otdRate.value) : "—",
        getNumber: (row) => row.ops?.otdRate.value ?? null,
        aggregate: "avg",
        format: formatPercent,
        projectByDays: false,
        goal: { value: 1.0, comparison: "gte", format: "percent" },
      },
      {
        key: "repairResizeDays",
        label: "Repair/Resize Days",
        getValue: (row) =>
          row.ops?.repairResizeDays.value != null ? row.ops.repairResizeDays.value.toFixed(1) : "—",
        getNumber: (row) => row.ops?.repairResizeDays.value ?? null,
        aggregate: "avg",
        format: (n) => n.toFixed(1),
        projectByDays: false,
        goal: { value: 7, comparison: "lte", format: "decimal" },
      },
    ],
  },
  {
    title: "Development (6 Star Client)",
    headerClass: "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
    rows: [
      {
        key: "nps_score",
        label: "NPS Score",
        getValue: (row) => (row.npsScore !== null ? row.npsScore.toFixed(1) : "—"),
        getNumber: (row) => row.npsScore,
        aggregate: "avg",
        format: (n) => n.toFixed(1),
        projectByDays: false,
        goal: { value: 96, comparison: "gte", format: "decimal" },
      },
      {
        key: "google_reviews",
        label: "Google Reviews",
        getValue: (row) => (row.googleReviews !== null ? formatNumber(row.googleReviews) : "—"),
        getNumber: (row) => row.googleReviews,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        goal: { value: 50, comparison: "gte", format: "count" },
      },
      {
        key: "affiliates_added",
        label: "Affiliates Added",
        getValue: (row) => (row.affiliatesAdded !== null ? formatNumber(row.affiliatesAdded) : "—"),
        getNumber: (row) => row.affiliatesAdded,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        goal: { value: 30, comparison: "gte", format: "count" },
      },
      {
        key: "created_bracelet_purchased",
        label: "Created Bracelet Purchased",
        manualKey: "created_bracelet_purchased",
        getValue: (row) =>
          row.manual.created_bracelet_purchased != null
            ? formatNumber(row.manual.created_bracelet_purchased)
            : "—",
        getNumber: (row) => row.manual.created_bracelet_purchased ?? null,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        yearEndGoal: { value: 300, format: "count" },
      },
      {
        key: "repeat_client",
        label: "Repeat Client",
        getValue: (row) => formatNumber(row.repeatClients),
        getNumber: (row) => row.repeatClients,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        yearEndGoal: { value: 2000, format: "count" },
      },
      {
        key: "fp_foundation_connect",
        label: "Connect with FP Foundation",
        manualKey: "fp_foundation_connect",
        getValue: (row) =>
          row.manual.fp_foundation_connect != null
            ? formatNumber(row.manual.fp_foundation_connect)
            : "—",
        getNumber: (row) => row.manual.fp_foundation_connect ?? null,
        aggregate: "sum",
        format: formatNumber,
        projectByDays: true,
        yearEndGoal: { value: 0.5, format: "percent" },
      },
    ],
  },
];

// ── Aggregate helpers ─────────────────────────────────────────────────

function aggregateRow(def: MetricRowDef, rows: MonthlyMetricsRow[]): string {
  const values = rows.map((row) => def.getNumber(row)).filter((v): v is number => v !== null);
  if (values.length === 0) return "—";
  const sum = values.reduce((a, b) => a + b, 0);
  const result = def.aggregate === "sum" ? sum : sum / values.length;
  return def.format(result);
}

function aggregateRowNumber(def: MetricRowDef, rows: MonthlyMetricsRow[]): number | null {
  const values = rows.map((row) => def.getNumber(row)).filter((v): v is number => v !== null);
  if (values.length === 0) return null;
  const sum = values.reduce((a, b) => a + b, 0);
  return def.aggregate === "sum" ? sum : sum / values.length;
}

function projectYearEnd(def: MetricRowDef, rows: MonthlyMetricsRow[]): string {
  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const completedRows = rows.filter((r) => r.month < currentMonthKey);
  const currentRow = rows.find((r) => r.month === currentMonthKey);

  if (def.aggregate === "avg") {
    const allValues = rows.map((r) => def.getNumber(r)).filter((v): v is number => v !== null);
    if (allValues.length === 0) return "—";
    return def.format(allValues.reduce((a, b) => a + b, 0) / allValues.length);
  }

  const completedValues = completedRows
    .map((r) => def.getNumber(r))
    .filter((v): v is number => v !== null);

  const completedSum = completedValues.reduce((a, b) => a + b, 0);
  const avgPerMonth = completedValues.length > 0 ? completedSum / completedValues.length : null;

  const currentValue = currentRow ? def.getNumber(currentRow) : null;
  let currentFullMonth = avgPerMonth;
  if (currentValue !== null) {
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    currentFullMonth = (currentValue / now.getDate()) * daysInMonth;
  }

  if (currentFullMonth === null) return "—";

  const monthsWithDataOrCurrent = completedValues.length + (currentRow ? 1 : 0);
  const remainingMonths = 12 - monthsWithDataOrCurrent;
  const pacePerMonth = avgPerMonth ?? currentFullMonth;

  const projected = completedSum + currentFullMonth + pacePerMonth * remainingMonths;
  return def.format(projected);
}

// ── Current Month Card ────────────────────────────────────────────────

function CurrentMonthMetricRow({
  def,
  row,
  scale,
  variant = "main",
  expanded,
  onToggle,
}: {
  def: MetricRowDef;
  row: MonthlyMetricsRow;
  scale: number;
  variant?: "main" | "detail";
  expanded?: boolean;
  onToggle?: () => void;
}) {
  const value = def.getNumber(row);
  const projected = value !== null && def.projectByDays ? value * scale : value;
  const goal = def.goal;
  const met =
    goal && value !== null
      ? goal.comparison === "gte"
        ? value >= goal.value
        : value <= goal.value
      : null;
  const projectedMet =
    goal && projected !== null
      ? goal.comparison === "gte"
        ? projected >= goal.value
        : projected <= goal.value
      : null;
  const remaining =
    goal && value !== null
      ? goal.comparison === "gte"
        ? Math.max(goal.value - value, 0)
        : goal.value - value
      : null;
  const metStatus = goal ? goalStatus(value, goal.value, goal.comparison) : "none";
  const projectedStatus = goal ? goalStatus(projected, goal.value, goal.comparison) : "none";
  const displayValue = value === null ? "—" : (def.getValue?.(row) ?? def.format(value));
  const displayProjected = projected === null ? "—" : def.format(projected);
  const hasDetails = variant === "main" && !!def.details?.length;
  const isDetail = variant === "detail";

  return (
    <TableRow className={isDetail ? "bg-muted/30" : undefined}>
      <TableCell
        className={cn(
          "whitespace-nowrap",
          isDetail ? "pl-12 text-sm text-muted-foreground" : "font-medium pl-6",
        )}
      >
        {hasDetails ? (
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-1.5 text-left hover:text-foreground"
          >
            <ChevronRight
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
                expanded && "rotate-90",
              )}
            />
            {def.label}
          </button>
        ) : (
          def.label
        )}
      </TableCell>
      <TableCell
        className={cn(
          "text-right whitespace-nowrap",
          isDetail ? "text-sm text-muted-foreground" : "font-semibold",
        )}
      >
        {displayValue}
      </TableCell>
      <TableCell
        className={cn(
          "text-right whitespace-nowrap",
          isDetail && "text-sm text-muted-foreground",
          GOAL_TEXT[projectedStatus],
        )}
      >
        {displayProjected}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap text-muted-foreground">
        {goal ? formatGoalValue(goal.value, goal.format) : "—"}
      </TableCell>
      <TableCell
        className={cn(
          "text-right whitespace-nowrap",
          isDetail ? "text-sm" : "font-semibold",
          GOAL_TEXT[metStatus],
        )}
      >
        {remaining === null
          ? "—"
          : met
            ? "Goal met"
            : goal?.comparison === "lte"
              ? `Over by ${formatGoalValue(Math.abs(remaining), goal.format)}`
              : formatGoalValue(remaining, goal?.format ?? "count")}
      </TableCell>
    </TableRow>
  );
}

function CurrentMonthCard({ row }: { row: MonthlyMetricsRow }) {
  const [expanded, setExpanded] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleRow = (key: string) =>
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // Each of the 5 sections collapses independently. Start with all open.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const toggleSection = (title: string) =>
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  const now = new Date();
  const total = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const elapsed = now.getDate();
  const scale = elapsed > 0 ? total / elapsed : 1;

  const _allRows = SECTIONS.flatMap((s) => s.rows);

  return (
    <Card>
      <CardHeader
        role="button"
        tabIndex={0}
        className="cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle>Current Month Performance</CardTitle>
            <p className="text-sm text-muted-foreground">
              {formatLongMonthLabel(row.month)} — FP Development · day {elapsed} of {total}
            </p>
          </div>
          <ChevronDown
            className={cn(
              "h-5 w-5 shrink-0 text-muted-foreground transition-transform",
              expanded && "rotate-180",
            )}
          />
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="p-0">
          <div className="space-y-6 p-4">
            {SECTIONS.map((section) => {
              const sectionOpen = !collapsedSections.has(section.title);
              return (
                <div key={section.title} className="overflow-hidden rounded-md border">
                  <button
                    type="button"
                    onClick={() => toggleSection(section.title)}
                    className={cn(
                      "flex w-full items-center justify-between gap-2 py-1.5 px-4 text-left text-xs font-semibold uppercase tracking-wide",
                      section.headerClass,
                    )}
                    aria-expanded={sectionOpen}
                  >
                    <span>{section.title}</span>
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 shrink-0 transition-transform",
                        !sectionOpen && "-rotate-90",
                      )}
                    />
                  </button>
                  {sectionOpen && (
                    <div className="overflow-x-auto">
                      <table className="w-full caption-bottom text-sm">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="bg-background pl-6">Metric</TableHead>
                            <TableHead className="bg-background text-right font-semibold whitespace-nowrap">
                              This Month
                            </TableHead>
                            <TableHead className="bg-background text-right font-semibold whitespace-nowrap">
                              Projected (Month-End)
                            </TableHead>
                            <TableHead className="bg-background text-right font-semibold whitespace-nowrap">
                              Monthly Goal
                            </TableHead>
                            <TableHead className="bg-background text-right font-semibold whitespace-nowrap">
                              Remaining to Goal
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {section.rows.map((def) => (
                            <Fragment key={def.key}>
                              <CurrentMonthMetricRow
                                def={def}
                                row={row}
                                scale={scale}
                                variant="main"
                                expanded={expandedRows.has(def.key)}
                                onToggle={() => toggleRow(def.key)}
                              />
                              {expandedRows.has(def.key) &&
                                def.details?.map((d) => (
                                  <CurrentMonthMetricRow
                                    key={d.key}
                                    def={d}
                                    row={row}
                                    scale={scale}
                                    variant="detail"
                                  />
                                ))}
                            </Fragment>
                          ))}
                        </TableBody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// Flatten all rows for use outside SECTIONS (unused but keeps TS happy)
const _allRows = SECTIONS.flatMap((s) => s.rows);
void _allRows;

// ── Editable manual-metric cell ───────────────────────────────────────

function ManualCell({
  month,
  metricKey,
  value,
  isAdmin,
  onSaved,
}: {
  month: string;
  metricKey: ManualMetricKey;
  value: number | undefined;
  isAdmin: boolean;
  onSaved: (month: string, metricKey: ManualMetricKey, value: number) => void;
}) {
  const [draft, setDraft] = useState(value !== undefined ? String(value) : "");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(value !== undefined ? String(value) : "");
  }, [value]);

  if (!isAdmin) {
    return (
      <TableCell className="text-center">
        {value !== undefined ? formatNumber(value) : "—"}
      </TableCell>
    );
  }

  const handleBlur = async () => {
    const trimmed = draft.trim();
    const num = trimmed === "" ? null : Number(trimmed);
    if (num === null || !Number.isFinite(num)) return;
    if (num === value) return;
    const res = await fetch("/api/company-monthly-metrics", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ month, metricKey, value: num }),
    });
    if (res.ok) {
      onSaved(month, metricKey, num);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    }
  };

  return (
    <TableCell className="p-1">
      <Input
        type="number"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        className={cn(
          "h-8 w-20 text-center transition-colors",
          saved && "border-green-500 ring-2 ring-green-500/20",
        )}
      />
    </TableCell>
  );
}

// ── Monthly table row (with optional expandable detail rows) ──────────

function MonthlyMetricRow({
  def,
  data,
  isAdmin,
  onSaved,
  variant = "main",
  expanded,
  onToggle,
}: {
  def: MetricRowDef;
  data: MonthlyMetricsResponse;
  isAdmin: boolean;
  onSaved: (month: string, metricKey: ManualMetricKey, value: number) => void;
  variant?: "main" | "detail";
  expanded?: boolean;
  onToggle?: () => void;
}) {
  const hasDetails = variant === "main" && !!def.details?.length;
  const isDetail = variant === "detail";

  return (
    <TableRow className={isDetail ? "bg-muted/30" : undefined}>
      <TableCell
        className={cn(
          "sticky left-0 z-10 bg-background whitespace-nowrap",
          isDetail ? "pl-12 text-sm text-muted-foreground" : "font-medium pl-6",
        )}
      >
        {hasDetails ? (
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-1.5 text-left hover:text-foreground"
          >
            <ChevronRight
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
                expanded && "rotate-90",
              )}
            />
            {def.label}
          </button>
        ) : (
          def.label
        )}
      </TableCell>
      {data.rows.map((row) =>
        def.manualKey ? (
          <ManualCell
            key={row.month}
            month={row.month}
            metricKey={def.manualKey}
            value={row.manual[def.manualKey]}
            isAdmin={isAdmin}
            onSaved={onSaved}
          />
        ) : (
          <TableCell
            key={row.month}
            className={cn("text-center", isDetail && "text-sm text-muted-foreground")}
          >
            {def.getValue?.(row) ?? "—"}
          </TableCell>
        ),
      )}
      <TableCell
        className={cn("text-center", isDetail ? "text-sm text-muted-foreground" : "font-semibold")}
      >
        {aggregateRow(def, data.rows)}
      </TableCell>
      <TableCell className="text-center font-semibold text-muted-foreground">
        {projectYearEnd(def, data.rows)}
      </TableCell>
      <TableCell
        className={cn("text-center", isDetail ? "text-sm text-muted-foreground" : "font-semibold")}
      >
        {def.yearEndGoal
          ? (def.yearEndGoal.label ??
            formatGoalValue(def.yearEndGoal.value, def.yearEndGoal.format))
          : "—"}
      </TableCell>
      {(() => {
        if (!def.yearEndGoal)
          return <TableCell className="text-center text-muted-foreground">—</TableCell>;
        const actual = aggregateRowNumber(def, data.rows);
        if (actual === null)
          return <TableCell className="text-center text-muted-foreground">—</TableCell>;
        const goal = def.yearEndGoal;
        const remaining = goal.value - actual;
        const met = remaining <= 0;
        return (
          <TableCell
            className={cn("text-center font-semibold", GOAL_TEXT[goalStatus(actual, goal.value)])}
          >
            {met ? "Goal met" : formatGoalValue(Math.abs(remaining), goal.format)}
          </TableCell>
        );
      })()}
    </TableRow>
  );
}

function YoyValueCells({ values }: { values: YoyMetricValues }) {
  return (
    <>
      <TableCell className="text-center border-l tabular-nums">
        {values.opportunitiesYoy != null ? formatPercent(values.opportunitiesYoy) : "—"}
      </TableCell>
      <TableCell className="text-center tabular-nums">
        {values.opportunities != null ? formatNumber(values.opportunities) : "—"}
      </TableCell>
      <TableCell className="text-center border-l tabular-nums">
        {values.closedDealsYoy != null ? formatPercent(values.closedDealsYoy) : "—"}
      </TableCell>
      <TableCell className="text-center tabular-nums">{formatNumber(values.closedDeals)}</TableCell>
      <TableCell className="text-center border-l tabular-nums">
        {values.grossSalesYoy != null ? formatPercent(values.grossSalesYoy) : "—"}
      </TableCell>
      <TableCell className="text-center tabular-nums">
        {formatCurrency(values.grossSales)}
      </TableCell>
      <TableCell className="text-center border-l tabular-nums">
        {values.netSalesYoy != null ? formatPercent(values.netSalesYoy) : "—"}
      </TableCell>
      <TableCell className="text-center tabular-nums">{formatCurrency(values.netSales)}</TableCell>
      <TableCell className="text-center border-l tabular-nums">
        {formatCurrency(values.avgOrderValue)}
      </TableCell>
      <TableCell className="text-center tabular-nums">
        {formatPercent(values.grossProfitMargin)}
      </TableCell>
      <TableCell className="text-center tabular-nums">
        {values.closeRate != null ? formatPercent(values.closeRate) : "—"}
      </TableCell>
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────

export function FPDevelopmentMonthlyTable() {
  const { data: session } = useSession();
  const isAdmin = (session?.user as { role?: string } | undefined)?.role === "admin";

  const [data, setData] = useState<MonthlyMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFY, setSelectedFY] = useState(currentFiscalYear());
  const [momExpanded, setMomExpanded] = useState(false);
  const [yoyData, setYoyData] = useState<YoyMetricsResponse | null>(null);
  const [yoyLoading, setYoyLoading] = useState(true);
  const [yoyError, setYoyError] = useState<string | null>(null);
  const [yoyExpanded, setYoyExpanded] = useState(false);
  const [expandedYoyYears, setExpandedYoyYears] = useState<Set<number>>(new Set());
  const toggleYoyYear = (year: number) =>
    setExpandedYoyYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  const [expandedDetailRows, setExpandedDetailRows] = useState<Set<string>>(new Set());
  const toggleDetailRow = (key: string) =>
    setExpandedDetailRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Each of the 5 KPI sections collapses independently. Start with all open.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const toggleSection = (title: string) =>
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/fp-development/monthly-metrics?fy=${selectedFY}`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const json = (await res.json()) as MonthlyMetricsResponse;
      if (json.error) throw new Error(json.error);
      setData(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load monthly metrics");
    } finally {
      setLoading(false);
    }
  }, [selectedFY]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const fetchYoyData = useCallback(async () => {
    setYoyLoading(true);
    setYoyError(null);
    try {
      const res = await fetch("/api/fp-development/yoy-metrics");
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const json = (await res.json()) as YoyMetricsResponse;
      if (json.error) throw new Error(json.error);
      setYoyData(json);
    } catch (err) {
      setYoyError(err instanceof Error ? err.message : "Failed to load year-over-year metrics");
    } finally {
      setYoyLoading(false);
    }
  }, []);

  // Year-over-Year scans the entire deal history, so only load it the first time
  // the (collapsed-by-default) section is actually opened — not on every page load.
  const yoyRequestedRef = useRef(false);
  useEffect(() => {
    if (yoyExpanded && !yoyRequestedRef.current) {
      yoyRequestedRef.current = true;
      fetchYoyData();
    }
  }, [yoyExpanded, fetchYoyData]);

  const availableFYs = [currentFiscalYear(), currentFiscalYear() - 1];

  const handleManualSaved = (month: string, metricKey: ManualMetricKey, value: number) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rows: prev.rows.map((row) =>
          row.month === month ? { ...row, manual: { ...row.manual, [metricKey]: value } } : row,
        ),
      };
    });
  };

  const fySwitcher = (
    <div className="flex flex-wrap items-center gap-2">
      {availableFYs.map((fy) => (
        <Button
          key={fy}
          type="button"
          size="sm"
          variant={selectedFY === fy ? "default" : "outline"}
          className={
            selectedFY === fy ? "bg-brand-gold text-brand-navy hover:bg-brand-gold/90" : undefined
          }
          onClick={() => setSelectedFY(fy)}
        >
          {formatFY(fy)}
        </Button>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* 6 Star Client Definition */}
      <div className="rounded-xl border-2 border-amber-400 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-500 p-5">
        <div className="flex items-start gap-3">
          <div className="flex gap-0.5 mt-0.5 shrink-0">
            {["s1", "s2", "s3", "s4", "s5", "s6"].map((id) => (
              <svg
                key={id}
                className="h-4 w-4 text-amber-500 fill-amber-400"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-amber-800 dark:text-amber-300 text-sm mb-2">
              What is a 6 Star Client?
            </h3>
            <p className="text-sm text-amber-700 dark:text-amber-400 leading-relaxed mb-3">
              A 6 Star Client is someone who received a world-class Faithful Platform experience —
              from first inquiry through delivery and beyond. They don't just love their ring; they
              become lifelong customers, walk away as FP brand ambassadors, and carry the values of
              a healthy marriage.
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wide mb-1.5">
                  What made them a 6 Star Client
                </p>
                <ul className="space-y-1">
                  {[
                    "One on one education",
                    "Excellent communication & expectations",
                    "A high quality ring",
                    "Exactly what they wanted",
                    "It fits perfectly",
                    "It arrived early",
                    "Delivered at the best price",
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400"
                    >
                      <svg
                        className="h-3 w-3 text-amber-500 fill-amber-400 shrink-0"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wide mb-1.5">
                  What they do next
                </p>
                <ul className="space-y-1">
                  {[
                    "Become a lifelong customer",
                    "Become an FP Walking Billboard",
                    "Become a Healthy Marriage Advocate",
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400"
                    >
                      <svg
                        className="h-3 w-3 text-amber-500 fill-amber-400 shrink-0"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.461a1 1 0 00.951-.69l1.07-3.292z M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-lg font-semibold text-foreground">Company Wide KPI's</h2>
        </div>
        {fySwitcher}
      </div>

      {loading && <Skeleton className="h-96" />}

      {!loading && (error || !data) && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {error ?? "No data available."}
        </div>
      )}

      {!loading &&
        data &&
        (() => {
          const now = new Date();
          const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
          const currentMonthRow = data.rows.find((r) => r.month === currentMonthKey);
          return currentMonthRow ? <CurrentMonthCard row={currentMonthRow} /> : null;
        })()}

      {!loading && data && (
        <Card>
          <CardHeader
            role="button"
            tabIndex={0}
            className="cursor-pointer select-none"
            onClick={() => setMomExpanded((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setMomExpanded((v) => !v);
              }
            }}
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <CardTitle>Month over Month KPIs</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Full {formatFY(data.fiscalYear)} breakdown by metric
                </p>
              </div>
              <ChevronDown
                className={cn(
                  "h-5 w-5 shrink-0 text-muted-foreground transition-transform",
                  momExpanded && "rotate-180",
                )}
              />
            </div>
          </CardHeader>
          {momExpanded && (
            <CardContent className="p-0">
              <div className="space-y-6 p-4">
                {SECTIONS.map((section) => {
                  const sectionOpen = !collapsedSections.has(section.title);
                  return (
                    <div key={section.title} className="overflow-hidden rounded-md border">
                      <button
                        type="button"
                        onClick={() => toggleSection(section.title)}
                        className={cn(
                          "flex w-full items-center justify-between gap-2 py-2 px-4 text-left font-semibold text-sm",
                          section.headerClass,
                        )}
                        aria-expanded={sectionOpen}
                      >
                        <span>{section.title}</span>
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 shrink-0 transition-transform",
                            !sectionOpen && "-rotate-90",
                          )}
                        />
                      </button>
                      {sectionOpen && (
                        <div className="overflow-x-auto">
                          <table className="w-full caption-bottom text-sm">
                            <TableHeader>
                              <TableRow>
                                <TableHead className="sticky left-0 z-20 bg-background min-w-[200px]">
                                  Metric
                                </TableHead>
                                {data.months.map((m) => (
                                  <TableHead
                                    key={m}
                                    className="bg-background text-center whitespace-nowrap"
                                  >
                                    {formatMonthLabel(m)}
                                  </TableHead>
                                ))}
                                <TableHead className="bg-background text-center font-semibold whitespace-nowrap">
                                  {formatFY(data.fiscalYear)} Total / Avg
                                </TableHead>
                                <TableHead className="bg-background text-center font-semibold whitespace-nowrap">
                                  Projected Year-End
                                </TableHead>
                                <TableHead className="bg-background text-center font-semibold whitespace-nowrap">
                                  Year End Goal
                                </TableHead>
                                <TableHead className="bg-background text-center font-semibold whitespace-nowrap">
                                  Remaining to Goal
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {section.rows.map((def) => (
                                <Fragment key={def.key}>
                                  <MonthlyMetricRow
                                    def={def}
                                    data={data}
                                    isAdmin={isAdmin}
                                    onSaved={handleManualSaved}
                                    variant="main"
                                    expanded={expandedDetailRows.has(def.key)}
                                    onToggle={() => toggleDetailRow(def.key)}
                                  />
                                  {expandedDetailRows.has(def.key) &&
                                    def.details?.map((d) => (
                                      <MonthlyMetricRow
                                        key={d.key}
                                        def={d}
                                        data={data}
                                        isAdmin={isAdmin}
                                        onSaved={handleManualSaved}
                                        variant="detail"
                                      />
                                    ))}
                                </Fragment>
                              ))}
                            </TableBody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      <Card>
        <CardHeader
          role="button"
          tabIndex={0}
          className="cursor-pointer select-none"
          onClick={() => setYoyExpanded((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setYoyExpanded((v) => !v);
            }
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle>Year Over Year</CardTitle>
              <p className="text-sm text-muted-foreground">
                Historical monthly performance and YOY change
              </p>
            </div>
            <ChevronDown
              className={cn(
                "h-5 w-5 shrink-0 text-muted-foreground transition-transform",
                yoyExpanded && "rotate-180",
              )}
            />
          </div>
        </CardHeader>
        {yoyExpanded && (
          <CardContent className="p-0">
            {yoyLoading && (
              <div className="p-4">
                <Skeleton className="h-96" />
              </div>
            )}
            {!yoyLoading && (yoyError || !yoyData) && (
              <div className="m-4 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
                {yoyError ?? "No data available."}
              </div>
            )}
            {!yoyLoading && yoyData && (
              <div className="rounded-md border overflow-x-auto max-h-[70vh] overflow-y-auto">
                <table className="w-full caption-bottom text-sm">
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        rowSpan={2}
                        className="sticky top-0 left-0 z-30 bg-background align-bottom min-w-[160px]"
                      >
                        Month
                      </TableHead>
                      <TableHead
                        colSpan={2}
                        className="sticky top-0 z-20 bg-background text-center border-l"
                      >
                        Opportunities
                      </TableHead>
                      <TableHead
                        colSpan={2}
                        className="sticky top-0 z-20 bg-background text-center border-l"
                      >
                        Orders
                      </TableHead>
                      <TableHead
                        colSpan={2}
                        className="sticky top-0 z-20 bg-background text-center border-l"
                      >
                        Gross Sales
                      </TableHead>
                      <TableHead
                        colSpan={2}
                        className="sticky top-0 z-20 bg-background text-center border-l"
                      >
                        Net Sales
                      </TableHead>
                      <TableHead
                        rowSpan={2}
                        className="sticky top-0 z-20 bg-background text-center align-bottom border-l whitespace-nowrap"
                      >
                        Avg Order Value
                      </TableHead>
                      <TableHead
                        rowSpan={2}
                        className="sticky top-0 z-20 bg-background text-center align-bottom whitespace-nowrap"
                      >
                        Gross Profit Margin %
                      </TableHead>
                      <TableHead
                        rowSpan={2}
                        className="sticky top-0 z-20 bg-background text-center align-bottom whitespace-nowrap"
                      >
                        Close Rate %
                      </TableHead>
                    </TableRow>
                    <TableRow>
                      <TableHead className="sticky top-8 z-20 bg-background text-center border-l whitespace-nowrap">
                        YOY per month
                      </TableHead>
                      <TableHead className="sticky top-8 z-20 bg-background text-center whitespace-nowrap">
                        Opportunities
                      </TableHead>
                      <TableHead className="sticky top-8 z-20 bg-background text-center border-l whitespace-nowrap">
                        YOY per month
                      </TableHead>
                      <TableHead className="sticky top-8 z-20 bg-background text-center whitespace-nowrap">
                        Orders
                      </TableHead>
                      <TableHead className="sticky top-8 z-20 bg-background text-center border-l whitespace-nowrap">
                        YOY per month
                      </TableHead>
                      <TableHead className="sticky top-8 z-20 bg-background text-center whitespace-nowrap">
                        Gross Sales
                      </TableHead>
                      <TableHead className="sticky top-8 z-20 bg-background text-center border-l whitespace-nowrap">
                        YOY per month
                      </TableHead>
                      <TableHead className="sticky top-8 z-20 bg-background text-center whitespace-nowrap">
                        Net Sales
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {yoyData.years.map((yr) => {
                      const isExpanded = expandedYoyYears.has(yr.year);
                      const yearBand =
                        yr.year % 2 === 0
                          ? "bg-blue-50/60 dark:bg-blue-950/20"
                          : "bg-muted/30 dark:bg-muted/20";
                      const monthRows = isExpanded
                        ? yoyData.rows
                            .filter((r) => r.month.startsWith(String(yr.year)))
                            .sort((a, b) => a.month.localeCompare(b.month))
                        : [];
                      return (
                        <Fragment key={yr.year}>
                          <TableRow
                            className={cn(yearBand, "cursor-pointer select-none")}
                            role="button"
                            tabIndex={0}
                            onClick={() => toggleYoyYear(yr.year)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                toggleYoyYear(yr.year);
                              }
                            }}
                          >
                            <TableCell
                              className={cn(
                                "sticky left-0 z-10 font-semibold whitespace-nowrap",
                                yearBand,
                              )}
                            >
                              <div className="flex items-center gap-1.5">
                                <ChevronRight
                                  className={cn(
                                    "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
                                    isExpanded && "rotate-90",
                                  )}
                                />
                                {yr.year}
                              </div>
                            </TableCell>
                            <YoyValueCells values={yr} />
                          </TableRow>
                          {monthRows.map((row) => (
                            <TableRow key={row.month} className={yearBand}>
                              <TableCell
                                className={cn(
                                  "sticky left-0 z-10 pl-8 text-muted-foreground whitespace-nowrap",
                                  yearBand,
                                )}
                              >
                                {formatLongMonthLabel(row.month)}
                              </TableCell>
                              <YoyValueCells values={row} />
                            </TableRow>
                          ))}
                          {yoyData.currentYearProjection &&
                            yoyData.currentYearProjection.year === yr.year && (
                              <TableRow className={cn(yearBand, "border-t border-dashed")}>
                                <TableCell
                                  className={cn(
                                    "sticky left-0 z-10 pl-8 italic text-muted-foreground whitespace-nowrap",
                                    yearBand,
                                  )}
                                >
                                  <div className="flex items-center gap-1.5">
                                    Projected Full Year
                                    <span className="rounded-full border border-amber-400 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium not-italic text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
                                      Projected
                                    </span>
                                  </div>
                                </TableCell>
                                <YoyValueCells values={yoyData.currentYearProjection} />
                              </TableRow>
                            )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </table>
              </div>
            )}
            {!yoyLoading && yoyData?.currentYearProjection && (
              <p className="px-4 py-3 text-xs text-muted-foreground">
                "Projected Full Year" estimates where {yoyData.currentYearProjection.year} will land
                based on pace so far — it's not a final number. The row above it shows real
                year-to-date totals.
              </p>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
